// Ported from Dealtracker src/appframe/tours/registry.ts (commit 9b8342a). Dealtracker filters on
// its `feature:verb` scopes; Niobe filters on `requires` (types.ts#TourRequirement).
import { useMemo } from 'react'
import { useAgentAvailable } from '../lib/agent-availability'
import { useBidProfile } from '../lib/bid-profile'
import { isDesktopApp } from '../lib/desktop'
import { parseRoute, usePath } from '../lib/router'
import { TOUR_DEFS } from './defs'
import type { TourArea, TourDef, TourRequirement } from './types'

/**
 * Public tour numbers by area: a tour's `number` lies in its area's range. Numbers are never
 * reused, even once a tour is removed.
 */
export const TOUR_NUMBER_RANGES: Record<TourArea, { from: number; to: number }> = {
  workspace: { from: 100, to: 199 }, // workspace and navigation
  prose: { from: 200, to: 299 },
  slides: { from: 300, to: 399 },
  grids: { from: 400, to: 499 },
  knowledge: { from: 500, to: 599 },
  ai: { from: 600, to: 699 },
  desktop: { from: 700, to: 799 },
  admin: { from: 800, to: 899 },
}

/** Reserved for the dev demo tour (guided tours part 2), whatever its area. */
export const DEV_DEMO_TOUR_NUMBER = 999

/** Numbers used by more than one tour, and tours whose number lies outside its area's range. */
export function tourNumberProblems(defs: readonly TourDef[]): string[] {
  const problems: string[] = []
  const seen = new Map<number, string>()
  for (const d of defs) {
    if (!Number.isInteger(d.number) || d.number <= 0) problems.push(`${d.id}: #${d.number} is not a positive integer`)
    const other = seen.get(d.number)
    if (other) problems.push(`#${d.number} is used by both ${other} and ${d.id}`)
    seen.set(d.number, d.id)
    if (d.number === DEV_DEMO_TOUR_NUMBER) continue
    const range = TOUR_NUMBER_RANGES[d.scope.area]
    if (d.number < range.from || d.number > range.to) {
      problems.push(`${d.id}: #${d.number} is outside the ${d.scope.area} range ${range.from}–${range.to}`)
    }
  }
  return problems
}

/** Which requirements the current user meets. A requirement still unknown is `false`. */
export type RequirementsMet = Record<TourRequirement, boolean>

/** Tours the user may see: every `requires` entry is met (fail closed). */
export function availableTours(defs: readonly TourDef[], met: RequirementsMet): TourDef[] {
  return defs.filter((t) => (t.requires ?? []).every((r) => met[r]))
}

/**
 * The requirements the current user meets, right now. `tenantAdmin` and `bidEditor` are false
 * until `/v1/me` has answered; `bidEditor` is false outside a bid.
 */
export function useRequirementsMet(): RequirementsMet {
  const { bid } = parseRoute(usePath())
  const profile = useBidProfile(bid)
  const agent = useAgentAvailable()
  const tenantAdmin = profile.isTenantAdmin
  const bidEditor = profile.isResolved && profile.canEdit
  return useMemo(
    () => ({ tenantAdmin, bidEditor, agent, desktop: isDesktopApp() }),
    [tenantAdmin, bidEditor, agent],
  )
}

/** The registered tours available to the current user (`defs` is for tests). */
export function useAvailableTours(defs: readonly TourDef[] = TOUR_DEFS): TourDef[] {
  const met = useRequirementsMet()
  return useMemo(() => availableTours(defs, met), [defs, met])
}
